import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { IconHome } from './Icons';

/** The partner portal is a separate application, so these are links out, not routes. */
const PARTNER_WEB_URL = import.meta.env.VITE_PARTNER_WEB_URL || 'http://localhost:5175';

export function Layout() {
  const { user, isAuthenticated, isOwner, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === '/login';

  // Collapse the mobile menu and return focus to the top on every navigation.
  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname, location.search]);

  const signOut = async () => {
    await logout();
    navigate('/');
  };

  return (
    <>
      <a className="sf-skip-link" href="#main">
        Skip to content
      </a>

      {!isLoginPage && <header className="sf-header">
        <div className="container d-flex align-items-center gap-3" style={{ minHeight: 'var(--sf-header-height)' }}>
          <Link to="/" className="sf-brand">
            <IconHome className="sf-brand__icon" aria-hidden="true" />
            <span>RoomKart</span>
          </Link>

          <button
            type="button"
            className="btn btn-sm btn-outline-secondary d-lg-none ms-auto"
            aria-expanded={menuOpen}
            aria-controls="primary-navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            Menu
          </button>

          <nav
            id="primary-navigation"
            className={`${menuOpen ? 'd-block' : 'd-none'} d-lg-flex align-items-center gap-1 ms-lg-auto w-100 w-lg-auto`}
            aria-label="Primary"
          >
            <div className="d-lg-flex align-items-center gap-1 py-2 py-lg-0">
              <NavLink className="btn btn-sm btn-link text-decoration-none d-block d-lg-inline" to="/pg">
                PG & Hostels
              </NavLink>
              <NavLink className="btn btn-sm btn-link text-decoration-none d-block d-lg-inline" to="/homes">
                Homes
              </NavLink>
              <NavLink className="btn btn-sm btn-link text-decoration-none d-block d-lg-inline" to="/hotels">
                Hotels
              </NavLink>

              {isAuthenticated ? (
                <>
                  <NavLink className="btn btn-sm btn-link text-decoration-none d-block d-lg-inline" to="/favourites">
                    Saved
                  </NavLink>
                  {isOwner && (
                    <a
                      className="btn btn-sm btn-outline-primary d-block d-lg-inline ms-lg-2"
                      href={PARTNER_WEB_URL}
                    >
                      Owner portal
                    </a>
                  )}
                  <div className="dropdown d-inline-block ms-lg-2">
                    <button
                      className="btn btn-sm btn-light dropdown-toggle"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {user?.fullName.split(' ')[0] ?? 'Account'}
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                      <li>
                        <Link className="dropdown-item" to="/account">
                          My account
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to="/enquiries">
                          My enquiries
                        </Link>
                      </li>
                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                      <li>
                        <button className="dropdown-item" type="button" onClick={signOut}>
                          Sign out
                        </button>
                      </li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <NavLink className="btn btn-sm btn-link text-decoration-none d-block d-lg-inline" to="/login">
                    Sign in
                  </NavLink>
                  <NavLink className="btn btn-sm btn-primary d-block d-lg-inline ms-lg-2" to="/register?type=owner">
                    List your property
                  </NavLink>
                </>
              )}
            </div>
          </nav>
        </div>
      </header>}

      <main id="main" className={`sf-page${isLoginPage ? ' sf-page--auth' : ''}`}>
        <Outlet />
      </main>

      {!isLoginPage && <footer className="sf-footer">
        <div className="container">
          <div className="row g-4">
            <div className="col-12 col-md-4">
              <div className="sf-brand d-block mb-2">RoomKart</div>
              <p className="mb-0">
                PG and hostels, homes for rent, and hotels in one place. Listings are published by their owners.
              </p>
            </div>

            <div className="col-6 col-md-2">
              <h2 className="h6">Browse</h2>
              <ul className="list-unstyled mb-0">
                <li>
                  <Link to="/pg">PG & Hostels</Link>
                </li>
                <li>
                  <Link to="/homes">Homes for rent</Link>
                </li>
                <li>
                  <Link to="/hotels">Hotels</Link>
                </li>
              </ul>
            </div>

            <div className="col-6 col-md-3">
              <h2 className="h6">For owners</h2>
              <ul className="list-unstyled mb-0">
                <li>
                  <Link to="/register?type=owner">List a property</Link>
                </li>
                <li>
                  <a href={PARTNER_WEB_URL}>Owner portal</a>
                </li>
                <li>
                  <Link to="/pricing">Pricing</Link>
                </li>
              </ul>
            </div>

            <div className="col-12 col-md-3">
              <h2 className="h6">Good to know</h2>
              <p className="mb-0 small">
                Prices and availability are entered by property owners. Always confirm them with the property before
                paying any deposit.
              </p>
            </div>
          </div>

          <hr />
          <p className="mb-0 small">
            &copy; {new Date().getFullYear()} RoomKart. All times are shown in India Standard Time.
          </p>
        </div>
      </footer>}
    </>
  );
}
